import { getFeatureFlag } from "../featureFlags";

export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "AIzaSyB9WNt4LOmGaa7-yC8cgNwXS_SBkZ7JQdI";

export const RIDE_MAP_PIN_COLORS = {
  pickup: "#2E7D32",
  dropoff: "#C62828",
};

function toStaticMapColor(hex) {
  return `0x${String(hex).replace("#", "")}`;
}

export const UBER_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f0f0f0" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
];

function stylerToStaticValue(value) {
  if (typeof value === "string" && value.startsWith("#")) {
    return `0x${value.slice(1)}`;
  }

  return value;
}

export function toStaticMapStyleParam(rule) {
  const parts = [];
  if (rule.featureType) {
    parts.push(`feature:${rule.featureType}`);
  }
  if (rule.elementType) {
    parts.push(`element:${rule.elementType}`);
  }
  (rule.stylers || []).forEach((styler) => {
    Object.keys(styler).forEach((key) => {
      parts.push(`${key}:${stylerToStaticValue(styler[key])}`);
    });
  });

  return parts.join("|");
}

function appendUberMapStyles(params) {
  UBER_MAP_STYLES.forEach((rule) => {
    params.append("style", toStaticMapStyleParam(rule));
  });
}

function normalizeRideStatus(status) {
  return String(status ?? "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function liveStatusSet(liveStatuses) {
  const statuses = Array.isArray(liveStatuses) ? liveStatuses : [];
  return new Set(statuses.map((status) => normalizeRideStatus(status)).filter(Boolean));
}

export function shouldUseLiveMap(
  status,
  liveStatuses = getFeatureFlag("rideMapLiveTrackingStatuses")
) {
  if (Array.isArray(liveStatuses) && liveStatuses.includes("*")) {
    return true;
  }

  const normalized = normalizeRideStatus(status);
  return Boolean(normalized) && liveStatusSet(liveStatuses).has(normalized);
}

function isFiniteCoordinate(coordinate) {
  if (coordinate === null || coordinate === undefined || coordinate === "") {
    return false;
  }

  return Number.isFinite(Number(coordinate));
}

export function hasRideMapCoordinates(item) {
  return [
    item?.latitudeFrom,
    item?.longitudeFrom,
    item?.latitudeTo,
    item?.longitudeTo,
  ].every(isFiniteCoordinate);
}

function encodeSigned(value) {
  let chunk = value < 0 ? ~(value << 1) : value << 1;
  let encoded = "";

  while (chunk >= 0x20) {
    encoded += String.fromCharCode((0x20 | (chunk & 0x1f)) + 63);
    chunk >>= 5;
  }

  return encoded + String.fromCharCode(chunk + 63);
}

export function encodePolyline(points) {
  let lastLat = 0;
  let lastLng = 0;
  let encoded = "";

  points.forEach((point) => {
    const lat = Math.round(point.lat * 1e5);
    const lng = Math.round(point.lng * 1e5);
    encoded += encodeSigned(lat - lastLat);
    encoded += encodeSigned(lng - lastLng);
    lastLat = lat;
    lastLng = lng;
  });

  return encoded;
}

export function staticMapSizeForContainer(width, height) {
  const maxEdge = 640;
  const safeWidth = Math.max(Number(width) || 0, 1);
  const safeHeight = Math.max(Number(height) || 0, 1);
  const aspect = safeWidth / safeHeight;

  if (aspect >= 1) {
    return {
      width: maxEdge,
      height: Math.max(1, Math.min(maxEdge, Math.round(maxEdge / aspect))),
    };
  }

  return {
    width: Math.max(1, Math.min(maxEdge, Math.round(maxEdge * aspect))),
    height: maxEdge,
  };
}

function clampStaticMapEdge(value) {
  const rounded = Math.round(Number(value));
  if (!Number.isFinite(rounded) || rounded < 1) {
    return 640;
  }

  return Math.min(640, rounded);
}

function buildPathParam(origin, destination, encodedPath) {
  if (encodedPath) {
    return `color:0x1A73E8FF|weight:6|enc:${encodedPath}`;
  }

  return `color:0x1A73E8FF|weight:6|geodesic:true|${origin}|${destination}`;
}

export function buildStaticRideMapUrl(item, options = {}) {
  if (!hasRideMapCoordinates(item)) {
    return null;
  }

  const origin = `${item.latitudeFrom},${item.longitudeFrom}`;
  const destination = `${item.latitudeTo},${item.longitudeTo}`;
  const width = clampStaticMapEdge(options.width);
  const height = clampStaticMapEdge(options.height);
  const params = new URLSearchParams({
    size: `${width}x${height}`,
    scale: "2",
    maptype: "roadmap",
    key: options.apiKey ?? GOOGLE_MAPS_API_KEY,
  });
  params.append(
    "markers",
    `color:${toStaticMapColor(RIDE_MAP_PIN_COLORS.pickup)}|label:A|${origin}`
  );
  params.append(
    "markers",
    `color:${toStaticMapColor(RIDE_MAP_PIN_COLORS.dropoff)}|label:B|${destination}`
  );
  params.append("path", buildPathParam(origin, destination, options.encodedPath));
  appendUberMapStyles(params);

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}
